/**
 * Main-thread persistence client (W1-13 persist-workers; contracted export `createPersistClient`,
 * contract persist/types.ts `PersistClient`; ARCHITECTURE.md §10 "Write path").
 *
 *  - Every committed txn goes to the persist worker at once (`appendTxn`, fire-and-forget) and stays in the
 *    OUTBOX until the worker acknowledges it as durable (`persisted {id, upToSeq}`).
 *  - The worker is watched: on `error` / `messageerror`, or when it stays silent for 5 s and then does not answer a
 *    probe within another 5 s (any message counts; silence while this thread was blocked does not; the worker
 *    also pulses inside the open path), the client terminates it, spawns a new one, re-opens every open project
 *    (waiting briefly for the dead worker's lock to be released) and re-posts the outbox from the first
 *    unacknowledged seq (the worker acknowledges seqs it already has). Calls that were in flight are retried on the
 *    new worker when they are idempotent. A failed fire-and-forget post is a failed call, not a dead worker: it
 *    never triggers a respawn.
 *  - Save health is computed HERE from acknowledgements (a dead worker cannot report itself healthy): pending
 *    txns, the age of the oldest unacknowledged one, worker liveness, plus the worker's storage facts.
 *  - `pagehide` / `visibilitychange` (hidden) flush and wait up to 1 s; a real unload (not bfcache) also asks the
 *    worker to close the open projects cleanly.
 *  - Compaction replay errors self-heal: the client snapshots the live document (registered with
 *    `attachLiveDoc`) and posts it with `writeSnapshot`.
 * Persistent storage (`navigator.storage.persist()`, window-only) is requested here, on the first project.
 */

import { deserializeDoc, serializeDoc } from '../core/doc/serialize';
import type { Json } from '../core/util/types';
import type { AssetId, ChartDoc, ProjectId } from '../core/model/types';
import type { Txn } from '../core/ops/types';
import type { PersistWorkerEvents, PersistWorkerSchema } from '../workers/protocol';
import { RemoteError, RpcTerminatedError, createRpcClientEx, type RpcClientEx } from '../workers/rpc';
import { PersistFailure, isPersistErrorCode, toPersistFailure, writeErrorText } from './errors';
import {
  EMPTY_FACTS,
  healthEquals,
  platformStorage,
  requestPersist,
  type StorageFacts,
  type StorageManagerLike,
} from './health';
import type {
  AnalysisKind,
  AnalysisMap,
  CreatePersistClient,
  OpenedProject,
  PersistClient,
  StorageHealth,
  VersionKind,
} from './types';

type Rpc = RpcClientEx<PersistWorkerSchema, PersistWorkerEvents>;
type ExternalEvent = Parameters<Parameters<PersistClient['onExternalChange']>[0]>[0];

export interface PersistClientOptions {
  /** Creates the persist worker (default: src/workers/persist.worker.ts as a module worker). */
  readonly spawn?: () => Worker;
  readonly now?: () => number;
  /**
   * Respawn after this long without a heartbeat (ARCHITECTURE §10: 5 s), also while a long call (open, close,
   * checkpoint, self-heal) is in flight: the worker pulses its heartbeat inside the open path (between phases, per
   * replayed txn, around the snapshot build), so a busy worker is never taken for a dead one.
   */
  readonly heartbeatTimeoutMs?: number;
  /**
   * How long an unanswered `health` probe (see the watchdog) may stay unanswered before the worker is replaced.
   * A worker busy in one long synchronous phase (the snapshot of a very large chart) answers the probe when that
   * phase ends; a dead one never does (UX-071, round-2: the default was one `heartbeatTimeoutMs`, so a long
   * compaction under load was taken for a crash). Defaults to `3 x heartbeatTimeoutMs`.
   */
  readonly probeTimeoutMs?: number;
  /**
   * How long a respawn may take to re-open the projects on the new worker before it is replaced again (the
   * watchdog does not run while restarting).
   */
  readonly restartTimeoutMs?: number;
  /** Health re-evaluation period while txns are pending or the worker is down. */
  readonly healthTickMs?: number;
  /** After a respawn, how long re-opening waits for the dead worker's project lock. */
  readonly reopenLockWaitMs?: number;
  /** Flush on pagehide / visibilitychange (default true where the events exist). */
  readonly pageLifecycle?: boolean;
  /** Where page lifecycle events are listened to (default: globalThis, i.e. the window). */
  readonly lifecycleTarget?: LifecycleTarget;
  readonly hideFlushWaitMs?: number;
  /** Main-thread StorageManager (persist() is window-only). */
  readonly storage?: StorageManagerLike | null;
}

/** The client plus its lifecycle (`attachLiveDoc` is a contract member since CR-W1-13-1). */
export interface PersistClientEx extends PersistClient {
  /** Terminates the worker and removes every timer and listener. */
  dispose(): void;
}

/** The part of the window the client listens to (pagehide, visibilitychange). */
export interface LifecycleTarget {
  addEventListener(type: string, cb: (e: Event) => void): void;
  removeEventListener(type: string, cb: (e: Event) => void): void;
  readonly document?: { readonly visibilityState?: string };
}

interface OutboxEntry {
  readonly txn: Txn;
  readonly postedAt: number;
  postedGen: number;
}

const RETRY_LIMIT = 3;
/** health(): how long to wait for fresh worker facts before answering from the client's view. */
const HEALTH_FACTS_WAIT_MS = 500;

function defaultSpawn(): Worker {
  return new Worker(new URL('../workers/persist.worker.ts', import.meta.url), {
    type: 'module',
    name: 'phiedit-persist',
  });
}

const sleep = (ms: number): Promise<void> =>
  new Promise((r) => {
    setTimeout(r, ms);
  });

/** `p`, or a PersistFailure 'io' after `ms` (the timer is cleared either way). */
function withDeadline<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new PersistFailure('io', message));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (err: unknown) => {
        clearTimeout(t);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

/** Maps an RPC rejection to a PersistFailure (codes survive the worker boundary). */
export function persistErrorOf(err: unknown): PersistFailure {
  if (err instanceof PersistFailure) return err;
  if (err instanceof RemoteError && isPersistErrorCode(err.code)) {
    return new PersistFailure(err.code, err.message, { cause: err });
  }
  if (err instanceof RpcTerminatedError) {
    return new PersistFailure('io', 'the storage worker restarted during the call', { cause: err });
  }
  return toPersistFailure(err);
}

export function createPersistClientEx(opts: PersistClientOptions = {}): PersistClientEx {
  const spawn = opts.spawn ?? defaultSpawn;
  const now = opts.now ?? (() => Date.now());
  const heartbeatTimeoutMs = opts.heartbeatTimeoutMs ?? 5000;
  const probeTimeoutMs = Math.max(heartbeatTimeoutMs, opts.probeTimeoutMs ?? heartbeatTimeoutMs * 3);
  const restartTimeoutMs = Math.max(heartbeatTimeoutMs, opts.restartTimeoutMs ?? 30_000);
  const healthTickMs = opts.healthTickMs ?? 500;
  const reopenLockWaitMs = opts.reopenLockWaitMs ?? 3000;
  const hideFlushWaitMs = opts.hideFlushWaitMs ?? 1000;
  const storage = opts.storage === undefined ? platformStorage() : opts.storage;

  const outbox = new Map<ProjectId, OutboxEntry[]>();
  const open = new Set<ProjectId>();
  const lost = new Set<ProjectId>();
  const liveDocs = new Map<ProjectId, () => { readonly doc: ChartDoc; readonly seq: number }>();
  const heldViews = new Map<ProjectId, Json>();
  const healthListeners = new Set<(h: StorageHealth) => void>();
  const externalListeners = new Set<(e: ExternalEvent) => void>();
  const ackWaiters = new Set<() => void>();

  let generation = 0;
  let workerFacts: StorageFacts = EMPTY_FACTS;
  let clientError: string | null = null;
  /**
   * A change posted for a project that is not open: it cannot be saved (no session acknowledges it), so it is not
   * queued; reported until the next successful open or close instead of keeping the save chip red for good.
   */
  let strayError: string | null = null;
  let lastFlushAt = 0;
  let lastBeat = now();
  /** When the watchdog last sent a liveness probe (a `health` call) to a silent worker, or 0. */
  let probedAt = 0;
  const alive = (): void => {
    lastBeat = now();
    probedAt = 0;
  };
  let respawning: Promise<void> | null = null;
  /** True from the moment a respawn starts until the new worker has re-opened the projects. */
  let restarting = false;
  let disposed = false;
  let lastHealth: StorageHealth | null = null;

  // ------------------------------ health ------------------------------

  const pendingCount = (filter?: (id: ProjectId) => boolean): number => {
    let n = 0;
    for (const [id, list] of outbox) if (filter === undefined || filter(id)) n += list.length;
    return n;
  };

  /** A project taken over by another tab can no longer be saved from here. */
  const lostError = (): string | null => {
    for (const id of lost) {
      if (open.has(id)) return `lockLost: project ${id} is being edited in another tab; changes here are not saved`;
    }
    return null;
  };

  const computeHealth = (): StorageHealth => {
    let pending = 0;
    let oldest = Number.POSITIVE_INFINITY;
    for (const list of outbox.values()) {
      pending += list.length;
      for (const e of list) if (e.postedAt < oldest) oldest = e.postedAt;
    }
    return {
      usageBytes: workerFacts.usageBytes,
      quotaBytes: workerFacts.quotaBytes,
      persisted: workerFacts.persisted,
      opfs: workerFacts.opfs,
      lastWriteError: workerFacts.lastWriteError ?? lostError() ?? clientError ?? strayError,
      lastFlushAt,
      pendingTxns: pending,
      oldestUnackedMs: pending === 0 ? 0 : Math.max(0, now() - oldest),
      workerAlive: !restarting && !disposed,
    };
  };

  const emitHealth = (): void => {
    const h = computeHealth();
    if (lastHealth !== null && healthEquals(lastHealth, h)) return;
    lastHealth = h;
    for (const cb of [...healthListeners]) cb(h);
  };

  const emitExternal = (e: ExternalEvent): void => {
    for (const cb of [...externalListeners]) cb(e);
  };

  // ------------------------------ acknowledgements ------------------------------

  const ack = (id: ProjectId, upToSeq: number): void => {
    const list = outbox.get(id);
    if (list !== undefined) {
      const rest = list.filter((e) => e.txn.seq > upToSeq);
      if (rest.length === 0) outbox.delete(id);
      else outbox.set(id, rest);
    }
    lastFlushAt = now();
    for (const w of [...ackWaiters]) w();
    emitHealth();
  };

  const waitForAck = (ms: number): Promise<void> =>
    new Promise((resolve) => {
      const done = (): void => {
        ackWaiters.delete(done);
        clearTimeout(t);
        resolve();
      };
      const t = setTimeout(done, ms);
      ackWaiters.add(done);
    });

  // ------------------------------ worker lifecycle ------------------------------

  const connect = (): Rpc => {
    const worker = spawn();
    const gen = ++generation;
    // Any message (heartbeat, acknowledgement, call result) shows the worker is alive (UX-071).
    worker.addEventListener('message', () => {
      if (gen === generation && !disposed) alive();
    });
    const c = createRpcClientEx<PersistWorkerSchema, PersistWorkerEvents>(worker);
    // A write error belongs to the worker that reported it; the new one announces its own facts on start.
    workerFacts = { ...workerFacts, lastWriteError: null };
    const current = (): boolean => gen === generation && !disposed;
    c.on('persisted', ({ id, upToSeq }) => {
      if (current()) ack(id, upToSeq);
    });
    c.on('heartbeat', () => {
      if (current()) alive();
    });
    c.on('health', (h) => {
      if (!current()) return;
      workerFacts = {
        usageBytes: h.usageBytes,
        quotaBytes: h.quotaBytes,
        persisted: h.persisted,
        opfs: h.opfs,
        lastWriteError: h.lastWriteError,
      };
      emitHealth();
    });
    c.on('lockLost', ({ id }) => {
      if (!current() || lost.has(id)) return;
      lost.add(id);
      emitExternal({ kind: 'lockLost', id });
      emitHealth();
    });
    c.on('external', (e) => {
      if (current()) emitExternal(e.id === undefined ? { kind: e.kind } : { kind: e.kind, id: e.id });
    });
    c.on('replayError', ({ id, error }) => {
      if (current()) selfHeal(id, error);
    });
    c.onFailure((reason) => {
      if (current()) void respawn(reason);
    });
    // A failed post (appendTxn, saveView, closeProject) is a failed call on a live worker, not a dead one: no
    // respawn. Unsaved txns stay in the outbox (pending in health) and the worker records its write failures in
    // its own facts, which are re-read so the save chip shows them now.
    c.onPostError(() => {
      if (current()) void pullFacts().then(emitHealth);
    });
    alive();
    return c;
  };

  let rpc: Rpc = connect();

  const postHeld = (): void => {
    for (const [id, list] of outbox) {
      if (lost.has(id)) continue;
      for (const e of [...list].sort((a, b) => a.txn.seq - b.txn.seq)) {
        if (e.postedGen === generation) continue;
        e.postedGen = generation;
        rpc.post('appendTxn', { id, txn: e.txn });
      }
    }
    for (const [id, view] of heldViews) rpc.post('saveView', { id, view });
    heldViews.clear();
  };

  /** Re-opens a project on a fresh worker; the dead worker's lock may take a moment to be released. */
  const reopen = async (c: Rpc, id: ProjectId): Promise<void> => {
    const deadline = now() + reopenLockWaitMs;
    for (;;) {
      try {
        const r = await c.call('openProject', { id });
        ack(id, r.seq);
        return;
      } catch (err) {
        const f = persistErrorOf(err);
        if (f.code === 'lockedElsewhere' && now() < deadline) {
          await sleep(100);
          continue;
        }
        if (f.code === 'lockedElsewhere' || f.code === 'lockLost' || f.code === 'notFound') {
          lost.add(id);
          emitExternal({ kind: 'lockLost', id });
          return;
        }
        throw err;
      }
    }
  };

  const respawn = (reason: string): Promise<void> => {
    if (restarting || disposed) return respawning ?? Promise.resolve();
    restarting = true;
    const old = rpc;
    clientError = null;
    const run = async (): Promise<void> => {
      old.terminate();
      emitHealth();
      for (let attempt = 0; !disposed; attempt++) {
        const c = connect();
        rpc = c;
        try {
          // A replacement that hangs as well must not stall the restart forever.
          await withDeadline(
            (async () => {
              for (const id of [...open]) if (!lost.has(id)) await reopen(c, id);
            })(),
            restartTimeoutMs,
            'the restarted storage worker does not answer',
          );
          return;
        } catch (err) {
          c.terminate();
          clientError = `io: the storage worker failed to restart (${reason}): ${persistErrorOf(err).message}`;
          emitHealth();
          await sleep(Math.min(10_000, 250 * 2 ** attempt));
        }
      }
    };
    respawning = run().finally(() => {
      respawning = null;
      restarting = false;
      if (!disposed) {
        clientError = null;
        alive();
        postHeld();
        emitHealth();
      }
    });
    return respawning;
  };

  /*
   * Watchdog (UX-071). A worker busy in one long synchronous phase (a snapshot of a very large chart under machine
   * load) cannot beat, and a main thread blocked for seconds cannot read the beats that wait in its queue, so:
   *  - a tick that comes much later than scheduled means THIS thread was blocked: the silence is not counted;
   *  - after `heartbeatTimeoutMs` of silence the worker is probed (a `health` call) and only replaced when that
   *    probe stays unanswered for `probeTimeoutMs` (a busy worker answers it when its phase ends; a dead one
   *    never does). A crash (`error` / `messageerror`) still replaces it at once.
   */
  const tickMs = Math.max(50, Math.min(healthTickMs, heartbeatTimeoutMs / 2));
  let lastTickAt = now();
  const watchdog = setInterval(() => {
    if (disposed) return;
    const t = now();
    const late = t - lastTickAt > tickMs * 4;
    lastTickAt = t;
    if (late) {
      // Main thread stalled: let the queued worker messages arrive before judging.
      lastBeat = Math.max(lastBeat, t - tickMs);
    } else if (!restarting && t - lastBeat > heartbeatTimeoutMs) {
      if (probedAt === 0) {
        probedAt = t;
        const probe = rpc;
        probe.call('health', {}).then(
          () => {
            if (probe === rpc) alive();
          },
          () => undefined,
        );
      } else if (t - probedAt > probeTimeoutMs) {
        probedAt = 0;
        void respawn('no heartbeat');
      }
    }
    if (pendingCount() > 0 || restarting) emitHealth();
  }, tickMs);

  // ------------------------------ calls ------------------------------

  const invoke = async <M extends keyof PersistWorkerSchema>(
    method: M,
    params: PersistWorkerSchema[M]['params'],
    retry: boolean,
  ): Promise<PersistWorkerSchema[M]['result']> => {
    for (let attempt = 0; ; attempt++) {
      if (disposed) throw new PersistFailure('io', 'the persistence client was disposed');
      if (respawning !== null) await respawning;
      try {
        return await rpc.call(method, params);
      } catch (err) {
        if (err instanceof RpcTerminatedError && retry && attempt < RETRY_LIMIT) continue;
        throw persistErrorOf(err);
      }
    }
  };

  const flushOutbox = async (only?: ProjectId): Promise<void> => {
    const inScope = (id: ProjectId): boolean => only === undefined || id === only;
    let stalled = 0;
    let before = Number.POSITIVE_INFINITY;
    for (;;) {
      const live = pendingCount((id) => inScope(id) && !lost.has(id));
      if (live === 0) {
        if (pendingCount((id) => inScope(id) && lost.has(id)) > 0) {
          throw new PersistFailure('lockLost', 'changes of a project taken over by another tab were not saved');
        }
        return;
      }
      if (pendingCount((id) => inScope(id) && !open.has(id)) > 0) {
        throw new PersistFailure('io', 'changes were queued for a project that is not open');
      }
      if (disposed) throw new PersistFailure('io', 'the persistence client was disposed');
      stalled = live < before ? 0 : stalled + 1;
      before = live;
      if (stalled > 20) throw new PersistFailure('io', 'the storage worker does not acknowledge the changes');
      if (respawning !== null) await respawning;
      try {
        await rpc.call('flush', {});
      } catch (err) {
        if (err instanceof RpcTerminatedError) continue;
        throw persistErrorOf(err);
      }
      if (pendingCount((id) => inScope(id) && !lost.has(id)) > 0) await waitForAck(250);
    }
  };

  const flushWithin = async (ms: number): Promise<void> => {
    await Promise.race([flushOutbox().catch(() => undefined), sleep(ms)]);
  };

  const selfHeal = (id: ProjectId, error: string): void => {
    const get = liveDocs.get(id);
    if (get === undefined || !open.has(id)) {
      clientError = `io: saved data of the project could not be replayed (${error})`;
      emitHealth();
      return;
    }
    const { doc, seq } = get();
    invoke('writeSnapshot', { id, doc: serializeDoc(doc), seq }, false).then(
      () => {
        if (clientError?.startsWith('io: saved data') === true) clientError = null;
        emitHealth();
      },
      (err: unknown) => {
        clientError = writeErrorText(err);
        emitHealth();
      },
    );
  };

  /** Re-reads the worker's storage facts (a refinement: never waits long on a silent worker). */
  async function pullFacts(): Promise<void> {
    try {
      const h = await withDeadline(invoke('health', {}, true), HEALTH_FACTS_WAIT_MS, 'storage worker did not answer');
      workerFacts = {
        usageBytes: h.usageBytes,
        quotaBytes: h.quotaBytes,
        persisted: h.persisted,
        opfs: h.opfs,
        lastWriteError: h.lastWriteError,
      };
    } catch {
      // Worker unreachable: the client-side view is red anyway.
    }
  }

  // ------------------------------ page lifecycle ------------------------------

  const g = (opts.lifecycleTarget ?? globalThis) as Partial<LifecycleTarget>;
  const onPageHide = (e: Event): void => {
    void flushWithin(hideFlushWaitMs);
    if ((e as { persisted?: boolean }).persisted !== true) {
      for (const id of open) if (!lost.has(id)) rpc.post('closeProject', { id });
    }
  };
  const onVisibility = (): void => {
    if (g.document?.visibilityState === 'hidden') void flushWithin(hideFlushWaitMs);
  };
  const lifecycle = opts.pageLifecycle !== false && typeof g.addEventListener === 'function';
  if (lifecycle) {
    g.addEventListener?.('pagehide', onPageHide);
    g.addEventListener?.('visibilitychange', onVisibility);
  }

  // ------------------------------ the client ------------------------------

  const client: PersistClientEx = {
    listProjects: (includeTrash = false) => invoke('listProjects', { includeTrash }, true),

    async createProject(doc, assets) {
      const first = (await invoke('listProjects', { includeTrash: true }, true)).length === 0;
      const id = await invoke(
        'createProject',
        {
          doc: serializeDoc(doc),
          assets: assets.map((a) => ({ id: a.id, bytes: a.bytes, mime: a.mime, role: a.role, meta: a.meta })),
        },
        false,
      );
      if (first) await client.requestPersistence().catch(() => false);
      return id;
    },

    async openProject(id): Promise<OpenedProject> {
      const r = await invoke('openProject', { id }, true);
      outbox.delete(id);
      open.add(id);
      lost.delete(id);
      strayError = null;
      emitHealth();
      return { ...r, doc: deserializeDoc(r.doc) };
    },

    async closeProject(id) {
      if (open.has(id) && !lost.has(id)) await flushOutbox(id);
      try {
        await invoke('closeProject', { id }, true);
        strayError = null;
      } finally {
        open.delete(id);
        lost.delete(id);
        outbox.delete(id);
        liveDocs.delete(id);
        heldViews.delete(id);
        emitHealth();
      }
    },

    takeOverLock: (id) => invoke('takeOverLock', { id }, true),

    appendTxn(id, txn) {
      if (!open.has(id)) {
        strayError = `io: change ${String(txn.seq)} of project ${id} was not saved: the project is not open`;
        emitHealth();
        return;
      }
      const list = outbox.get(id) ?? [];
      const entry: OutboxEntry = { txn, postedAt: now(), postedGen: -1 };
      list.push(entry);
      outbox.set(id, list);
      if (!restarting && !disposed && !lost.has(id)) {
        entry.postedGen = generation;
        rpc.post('appendTxn', { id, txn });
      }
      emitHealth();
    },

    flush: () => flushOutbox(),

    async checkpoint(id, label, kind: VersionKind = 'checkpoint') {
      if (open.has(id)) await flushOutbox(id);
      return invoke('checkpoint', { id, label, kind }, false);
    },

    listVersions: (id) => invoke('listVersions', { id }, true),

    async loadVersion(id, seq) {
      return deserializeDoc(await invoke('loadVersion', { id, seq }, true));
    },

    async duplicateProject(id, o) {
      if (open.has(id)) await flushOutbox(id);
      return invoke(
        'duplicateProject',
        {
          id,
          newDifficulty: o.newDifficulty,
          ...(o.name === undefined ? {} : { name: o.name }),
          ...(o.level === undefined ? {} : { level: o.level }),
          ...(o.difficulty === undefined ? {} : { difficulty: o.difficulty }),
        },
        false,
      );
    },

    async renameProject(id, name) {
      await invoke('renameProject', { id, name }, true);
    },
    async trashProject(id) {
      await invoke('trashProject', { id }, true);
    },
    async restoreProject(id) {
      await invoke('restoreProject', { id }, true);
    },
    async deleteForever(id) {
      await invoke('deleteForever', { id }, false);
    },

    putAsset: (bytes, mime, role, meta) => invoke('putAsset', { bytes, mime, role, meta }, true),
    getAsset: (id) => invoke('getAsset', { id }, true),
    hasAsset: (id) => invoke('hasAsset', { id }, true),

    saveView(id, view) {
      if (restarting) {
        heldViews.set(id, view);
        return;
      }
      rpc.post('saveView', { id, view });
    },

    kvGet: (key) => invoke('kvGet', { key }, true),
    async kvSet(key, value) {
      await invoke('kvSet', { key, value }, true);
    },

    async analysisGet<K extends AnalysisKind>(audio: AssetId, kind: K): Promise<AnalysisMap[K] | undefined> {
      return (await invoke('analysisGet', { audio, kind }, true)) as AnalysisMap[K] | undefined;
    },
    async analysisSet(audio, kind, value) {
      await invoke('analysisSet', { audio, kind, value }, true);
    },

    listSkins: () => invoke('listSkins', {}, true),
    getSkin: (id) => invoke('getSkin', { id }, true),
    putSkin: (pack) => invoke('putSkin', { pack }, true),
    async deleteSkin(id) {
      await invoke('deleteSkin', { id }, true);
    },

    async health() {
      await pullFacts();
      const h = computeHealth();
      lastHealth = h;
      return h;
    },

    onHealth(cb) {
      healthListeners.add(cb);
      return () => {
        healthListeners.delete(cb);
      };
    },

    async requestPersistence() {
      const granted = await requestPersist(storage);
      const workerView = await invoke('requestPersistence', {}, true).catch(() => false);
      return granted ?? workerView;
    },

    recoveryData: (id) => invoke('recoveryData', { id }, true),

    onExternalChange(cb) {
      externalListeners.add(cb);
      return () => {
        externalListeners.delete(cb);
      };
    },

    attachLiveDoc(id, get) {
      liveDocs.set(id, get);
      return () => {
        if (liveDocs.get(id) === get) liveDocs.delete(id);
      };
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      clearInterval(watchdog);
      if (lifecycle) {
        g.removeEventListener?.('pagehide', onPageHide);
        g.removeEventListener?.('visibilitychange', onVisibility);
      }
      rpc.terminate();
      for (const w of [...ackWaiters]) w();
    },
  };
  return client;
}

export const createPersistClient: CreatePersistClient = () => createPersistClientEx();
